import { useState } from 'react';

import { AccessTime, ExitToApp, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { addBranchSend } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { getEffectiveSplitType, getBranchRootParentIndex } from 'domains/campaigns/components/CustomCampaign/utils';
import { SplitTypes } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

import { DisabledTooltip } from '../../../components/DisabledTooltipContent';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';
import { MAX_SENDS_AMOUNT } from '../utils';

const EXIT_FLOW_ACTION = 'exit-flow';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const findDoesNotMeetSendIndex = (sends: CampaignStateTypes['sends'], parentIndex: number): number =>
    sends.findIndex(
        (send: any) => send.splitType === SplitTypes.DOES_NOT_MEET && (send.parentIndex ?? 0) === parentIndex
    );

/**
 * Intermediate "choose next step" screen shown after completing an email
 * within a conditional split branch (meets / does not meet criteria).
 *
 * Options:
 *  - Time delay: add another email to the current branch (chained via parentIndex)
 *  - Exit flow:  proceed to the other branch or finish the conditional split
 */
const CustomCampaignChooseNextStepConditionalSplit = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState<string | null>(null);
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const isEditingPausedCampaign = status === CampaignStatus.PAUSED;

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const maxSendsReached = campaignState.sends.length >= MAX_SENDS_AMOUNT;
    const isTimeDelayDisabled = maxSendsReached || isEditingPausedCampaign;

    const effectiveSplitType = getEffectiveSplitType(activeSend, campaignState.sends);
    const isMeetsCriteriaBranch = effectiveSplitType === SplitTypes.MEETS;
    const branchRootParentIndex = getBranchRootParentIndex(activeSend, campaignState.sends);

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value;
        setSelectedStep(step);

        if (step === CustomCampaignSteps.SET_TIME_DELAY) {
            const branchSplitType = isMeetsCriteriaBranch ? SplitTypes.MEETS : SplitTypes.DOES_NOT_MEET;

            dispatchCampaignAction(addBranchSend(activeSendIndex, branchSplitType));

            if (isMeetsCriteriaBranch) {
                const doesNotMeetIndex = findDoesNotMeetSendIndex(campaignState.sends, branchRootParentIndex);
                setActiveSendIndex(doesNotMeetIndex !== -1 ? doesNotMeetIndex : campaignState.sends.length);
            } else {
                setActiveSendIndex(campaignState.sends.length);
            }

            setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
        }

        if (step === EXIT_FLOW_ACTION) {
            if (isMeetsCriteriaBranch) {
                const doesNotMeetIndex = findDoesNotMeetSendIndex(campaignState.sends, branchRootParentIndex);
                if (doesNotMeetIndex !== -1) {
                    setActiveSendIndex(doesNotMeetIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }
            } else {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
            }
        }
    };

    const branchLabel = isMeetsCriteriaBranch ? 'meets' : 'does not meet';
    const exitLabel = isMeetsCriteriaBranch ? 'proceed to "does not meet criteria"' : 'done with conditional split';

    const guidingText = `You've completed an email for the "${branchLabel} the criteria" path. Choose to add another time-delayed email to this path, or proceed to the next step.`;

    const steps = [
        {
            disabled: isTimeDelayDisabled,
            disabledMessage: isEditingPausedCampaign ? (
                <DisabledTooltip />
            ) : (
                "You've reached the maximum number of sends."
            ),
            icon: <AccessTime sx={iconStyles} />,
            label: 'time delay',
            value: CustomCampaignSteps.SET_TIME_DELAY,
        },
        {
            disabled: false,
            disabledMessage: '',
            icon: <ExitToApp sx={iconStyles} />,
            label: exitLabel,
            value: EXIT_FLOW_ACTION,
        },
    ];

    return (
        <FormLayout guidingText={guidingText} header="choose next step" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleStepClick} value={selectedStep}>
                {steps.map((step) => (
                    <Tooltip key={step.value} title={step.disabled ? step.disabledMessage : ''}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{
                                ...sharedButtonStyles,
                                ...(step.disabled && disabledButtonStyles),
                            }}
                            value={step.value}
                        />
                    </Tooltip>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseNextStepConditionalSplit;
